import { DynamoDBVocabularyResponse, VocabularyItem, transformVocabularyData } from '../types/api';

export interface CreateVocabularyRequest {
    Japanese_word: string;
    English_word: string;
}

export interface CreateVocabularyResponse {
    message: string;
}

export interface ApiError {
    message: string;
    status?: number;
}

export class VocabularyApi {
    private baseUrl: string;

    constructor(
        baseUrl: string = 'https://2u15ibhh9c.execute-api.ap-southeast-2.amazonaws.com/live',
    ) {
        this.baseUrl = baseUrl;
    }

    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit,
        accessToken: string,
    ): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`;

        const response = await fetch(url, {
            ...options,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...options.headers,
            },
            mode: 'cors',
            credentials: 'omit',
        });

        if (!response.ok) {
            const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
            (error as any).status = response.status;
            throw error;
        }

        // Check if response has content before trying to parse JSON
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');

        // If no content or content-length is 0, return empty object for successful responses
        if (contentLength === '0' || !contentType?.includes('application/json')) {
            return {} as T;
        }

        // Read response as text first to avoid "body stream already read" error
        const responseText = await response.text();

        // If response is empty, return empty object for successful responses
        if (!responseText.trim()) {
            return {} as T;
        }

        try {
            const jsonResponse = JSON.parse(responseText);
            return jsonResponse;
        } catch (jsonError) {
            const errorMessage =
                jsonError instanceof Error ? jsonError.message : 'Unknown JSON error';
            const truncatedResponse = responseText.substring(0, 200);
            const error = new Error(
                `Failed to parse JSON response: ${errorMessage}. ` +
                    `Response text: ${truncatedResponse}...`,
            );
            throw error;
        }
    }

    /**
     * Fetch all vocabulary items from the API
     */
    async getVocabulary(accessToken: string): Promise<VocabularyItem[]> {
        try {
            const data = await this.makeRequest<DynamoDBVocabularyResponse>(
                '/japanesevocabulary',
                { method: 'GET' },
                accessToken,
            );

            return transformVocabularyData(data);
        } catch (error) {
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('Unknown error occurred');
        }
    }

    /**
     * Create a new vocabulary entry
     */
    async createVocabulary(
        vocabularyData: CreateVocabularyRequest,
        accessToken: string,
    ): Promise<CreateVocabularyResponse> {
        try {
            const response = await this.makeRequest<CreateVocabularyResponse>(
                '/japanesevocabulary',
                {
                    method: 'POST',
                    body: JSON.stringify(vocabularyData),
                },
                accessToken,
            );
            return response;
        } catch (error) {
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('Unknown error occurred while adding word');
        }
    }
}

// Export a singleton instance
export const vocabularyApi = new VocabularyApi();
